import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/native';

import { DEMO_LOGINS, ELoginAs, LIVE_LOGINS } from '@webtrader/core/constants/account';
import { EDataTestId } from '@webtrader/core/constants/test';
import dynamicSize from '@webtrader/core/dynamic-size';
import { ILoginCredentialsFormValue } from '@webtrader/core/format/login';
import { useLoginInputLabel } from '@webtrader/core/hooks/auth/useLoginInputLabel';
import { useAuth } from '@webtrader/core/services/auth';
import { isEmpty, isValidEmail } from '@webtrader/core/validators';
import { checkIsNative } from '@webtrader/core/validators/device';

import Button from '@/components/Button';
import ValidationError from '@/components/FormValidationError';
import TextInput from '@/components/TextInput';

import Biometrics from './Biometrics';
import { useBiometrics } from './Hooks/useBiometrics';
import RememberMe from './RememberMe';

interface IAuthLoginFormProps {
  isDemo?: boolean;
  isShowSubTab?: boolean;
  needUpdateFields: boolean;
  selectedTab: ELoginAs;
  values: ILoginCredentialsFormValue;
  setFieldValue: <FieldName extends keyof ILoginCredentialsFormValue>(
    fieldName: FieldName,
    value: ILoginCredentialsFormValue[FieldName]
  ) => void;
  setNeedUpdateFields: (value: boolean) => void;
  submitForm: () => void;
}

const IS_MOBILE_WEB = !checkIsNative();

const AuthLoginForm: FC<IAuthLoginFormProps> = (props) => {
  const {
    isDemo = false,
    isShowSubTab = false,
    needUpdateFields,
    selectedTab,
    values,
    setFieldValue,
    setNeedUpdateFields,
    submitForm
  } = props;

  const { t } = useTranslation();
  const {
    usernamePrefilled,
    passwordPrefilled,
    usernamePrefilledFromStorage,
    passwordPrefilledFromStorage,
    loading
  } = useAuth();
  const { isBiometricEnable } = useBiometrics();

  const { userId, password } = values;

  const onUserIdChange = useCallback(
    (nextValue: string) => {
      setNeedUpdateFields(false);
      setFieldValue('userId', nextValue);
    },
    [setNeedUpdateFields, setFieldValue]
  );

  const onPasswordChange = useCallback(
    (nextValue: string) => {
      setNeedUpdateFields(false);
      setFieldValue('password', nextValue);
    },
    [setNeedUpdateFields, setFieldValue]
  );

  const userIdInputLabel = useLoginInputLabel(isShowSubTab, selectedTab);

  const isEmailError = useMemo(() => {
    if (isShowSubTab) {
      return userId ? !isValidEmail(userId) : !isEmpty(userId);
    }

    return false;
  }, [isShowSubTab, userId]);

  const disabledSubmit = useMemo(() => {
    return isEmpty(userId) || isEmpty(password) || isEmailError;
  }, [userId, password, isEmailError]);

  useEffect(() => {
    if (loading || !needUpdateFields) {
      return;
    }

    if (LIVE_LOGINS.includes(selectedTab)) {
      onUserIdChange(usernamePrefilledFromStorage ? String(usernamePrefilledFromStorage) : '');
      onPasswordChange(passwordPrefilledFromStorage ?? '');
    } else if (DEMO_LOGINS.includes(selectedTab)) {
      onUserIdChange(usernamePrefilled ? String(usernamePrefilled) : '');
      onPasswordChange(passwordPrefilled ?? '');
    } else {
      onUserIdChange('');
      onPasswordChange('');
    }
  }, [
    loading,
    selectedTab,
    needUpdateFields,
    usernamePrefilledFromStorage,
    passwordPrefilledFromStorage,
    usernamePrefilled,
    passwordPrefilled,
    onUserIdChange,
    onPasswordChange
  ]);

  return (
    <Form>
      <FormField>
        <TextInput
          label={userIdInputLabel}
          value={userId}
          keyboardType={selectedTab === ELoginAs.LIVE ? 'email-address' : 'default'}
          onChangeText={onUserIdChange}
          testID={EDataTestId.LOGIN_USER_ID}
        />
        <ValidationError showValidation={isEmailError} message={t('Email format is invalid')} />
      </FormField>
      <FormField>
        <TextInput
          label={t('Password')}
          value={password}
          secureTextEntry={true}
          onChangeText={onPasswordChange}
          testID={EDataTestId.LOGIN_PASSWORD}
        />
      </FormField>
      {!IS_MOBILE_WEB && !isDemo && (
        <FormField>
          <RememberMe />
        </FormField>
      )}
      <FormActions>
        <LoginButton
          disabled={disabledSubmit}
          loading={!disabledSubmit && loading}
          onPress={submitForm}
          testID={EDataTestId.LOGIN_SUBMIT}
        >
          {t('SIGN IN')}
        </LoginButton>
        {isBiometricEnable && <Biometrics isDemo={isDemo} loading={disabledSubmit && loading} />}
      </FormActions>
    </Form>
  );
};

export default AuthLoginForm;

const Form = styled.View`
  padding-top: ${dynamicSize.convertSize(30)}px;
  padding-bottom: ${dynamicSize.convertSize(10)}px;
`;

const FormField = styled.View`
  padding-bottom: ${dynamicSize.convertSize(10)}px;
`;

const FormActions = styled.View`
  flex-direction: row;
  padding-top: ${dynamicSize.convertSize(10)}px;
`;

const LoginButton = styled(Button)`
  flex: 1;
`;
